import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { DateTime } from "luxon";
import { environment } from "src/environments/environment";
import { DataService } from "./data.service";
import { DateRangeFilter, FilterService } from "./filter.service";
import { EngageReferralsResponse } from "./engage-activity.service";
import {
  appendPatientDirectoryFiltersToUrl,
  parsePatientDirectoryOrLegacyReferralsPayload,
  patientDirectoryAssignedToQueryValue,
  patientDirectoryReferralSourceQueryValue,
  phiPatientDirectoryListGetUrl,
  type PatientDirectoryFilterValues,
} from "../utils/phi-patient-directory-list.util";

/** Dashboard unread + Engage may use `sms` and/or `message` for the same bucket. */
function expandNotificationTypesForReferralsQuery(values: string[]): string[] {
  const out = new Set<string>();
  for (const raw of values) {
    const t = String(raw ?? "").trim();
    if (!t) {
      continue;
    }
    out.add(t);
    if (t.toLowerCase() === "sms") {
      out.add("message");
    }
  }
  return [...out];
}

/** Optional per-request overrides for patient directory list (list + meta). */
export interface EngageReferralsQueryPageOptions {
  /**
   * Referral `startDate`/`endDate` for this request only.
   * - `undefined` — use {@link FilterService.getAppliedDateRange}
   * - `null` — omit dates (all-time window)
   */
  referralDateRangeOverride?: DateRangeFilter | null;
  /** PHI directory `engage_search` — filters list rows by patient name (Activity sidebar search). */
  engageSearch?: string;
  /** With engage_search: request full directory rows (`engage_search_full=true`) instead of slim names-only rows. */
  engageSearchFull?: boolean;
}

/**
 * Loads PHI `GET api/v1/patients/directory?page=&limit=` (filters as query params),
 * including list-clinic pre-step when DOS date range and/or encounter status filters are applied.
 */
@Injectable({
  providedIn: "root",
})
export class EngageReferralsQueryService {
  constructor(
    private readonly _dataService: DataService,
    private readonly _filterService: FilterService
  ) {}

  getReferralsPage(
    page: number,
    limit: number,
    opts?: EngageReferralsQueryPageOptions
  ): Observable<EngageReferralsResponse> {
    return this.prepareListClinicPatientIdsIfNeeded().pipe(
      switchMap(() => this.getReferralsAfterListClinic(page, limit, opts))
    );
  }

  /**
   * POST list-clinic (same payload shape as Schedule / referrals) to resolve patient IDs for the directory
   * when DOS range and/or encounter status is applied — directory filters use resolved patient ids.
   */
  private prepareListClinicPatientIdsIfNeeded(): Observable<void> {
    if (!this._filterService.isEngageListClinicPreFilterApplied) {
      return of(undefined);
    }
    const f = this._filterService.getDosFilters();
    let startDate: DateTime;
    let endDate: DateTime;
    if (f.dos?.length === 2) {
      startDate = DateTime.fromJSDate(f.dos[0]).startOf("day");
      endDate = DateTime.fromJSDate(f.dos[1]).endOf("day");
    } else {
      // Encounter-status-only: list-clinic requires filterDate — use a wide window (same idea as unpaged counts).
      endDate = DateTime.now().endOf("day");
      startDate = endDate.minus({ years: 10 }).startOf("day");
    }
    const filterStatus = (f.encounterStatuses || []).map((s) => String(s.value || "").trim()).filter(Boolean);
    const payload = {
      filterDate: {
        startDate: startDate.toISO({
          includeOffset: false,
          suppressMilliseconds: true,
        }),
        endDate: endDate
          .set({ millisecond: 0 })
          .toISO({ includeOffset: false, suppressMilliseconds: true }),
      },
      resources: f.resource ? [f.resource.value] : [],
      filterLocation: f.location ? [f.location.name] : [],
      filterStatus,
      visitType: [],
      pageStart: 0,
      pageLength: 10_000,
      sortField: "encounterDateTime",
      sortDirection: "DESC",
      searchQuery: "",
    };
    const url = `${environment.apiBaseUrl}api/v1/encounters/list-clinic`;
    return this._dataService.post(url, payload).pipe(
      tap((response: any) => {
        this._filterService.setDosFiltersResult(
          response?.data ? response.data.map((p: { patientId: string }) => p.patientId) : []
        );
      }),
      map(() => undefined)
    );
  }

  private getPatientDirectoryPage(
    page: number,
    limit: number,
    filters: PatientDirectoryFilterValues
  ): Observable<EngageReferralsResponse> {
    const base = phiPatientDirectoryListGetUrl(page, limit);
    const url = appendPatientDirectoryFiltersToUrl(base, filters);
    return this._dataService.get(url).pipe(
      tap((response: unknown) => {
        const rec = response as { filterCounts?: typeof this._filterService.filterCounts };
        if (rec.filterCounts != null) {
          this._filterService.filterCounts = rec.filterCounts;
        }
      }),
      map((res: unknown) => parsePatientDirectoryOrLegacyReferralsPayload(res, page, limit))
    );
  }

  private mergeReferralCreatedDateRangeIntoFilters(
    filters: PatientDirectoryFilterValues,
    opts?: EngageReferralsQueryPageOptions
  ): void {
    const dr =
      opts?.referralDateRangeOverride !== undefined
        ? opts.referralDateRangeOverride
        : this._filterService.getAppliedDateRange();
    if (!dr?.startDate || !dr?.endDate) {
      return;
    }
    filters.startDate = this.getFormattedStartDateString(dr.startDate);
    filters.endDate = this.getFormattedEndDateString(dr.endDate);
  }

  private buildPatientDirectoryQueryFilters(
    opts?: EngageReferralsQueryPageOptions
  ): PatientDirectoryFilterValues {
    const filters: PatientDirectoryFilterValues = {};
    const fs = this._filterService;

    if (fs.getAppliedStages().length > 0) {
      const stages = fs
        .getAppliedStages()
        .map((s) => String(s.value ?? "").trim().toLowerCase())
        .filter(Boolean);
      if (stages.length > 0) {
        filters.stage = stages.join(",");
      }
    }

    const referralSourceIds = fs
      .getAppliedChannels()
      .map((s) => patientDirectoryReferralSourceQueryValue(String(s.value ?? "")))
      .filter((v) => v.length > 0);
    if (referralSourceIds.length > 0) {
      filters.referral_source = referralSourceIds.join(",");
    }

    if (fs.getAppliedNotificationTypes().length > 0 && fs.getReferralNotificationFilterKeys().length === 0) {
      const notifyTypes = expandNotificationTypesForReferralsQuery(
        fs
          .getAppliedNotificationTypes()
          .map((s) => String(s.value ?? "").trim())
          .filter(Boolean)
      );
      filters.notification_type = notifyTypes.join(",");
    }

    if (fs.getAppliedStatuses().length > 0) {
      const status = fs
        .getAppliedStatuses()
        .map((s) => String(s.value ?? "").trim().toLowerCase())
        .filter(Boolean);
      filters.lead_status = status.join(",");
    }

    if (fs.getAppliedReferralIds().length > 0) {
      filters.ids = fs.getAppliedReferralIds().join(",");
    }

    const worklistPatientId = fs.getWorklistEngagePatientIdFilter?.() ?? "";
    if (
      worklistPatientId &&
      fs.getAppliedReferralIds().length === 0 &&
      !fs.isEngageListClinicPreFilterApplied
    ) {
      filters.patientId = worklistPatientId;
    }

    const assignedCsv = fs
      .getAppliedAssignedTo()
      .map((a) => String(a.value ?? "").trim())
      .filter(Boolean)
      .join(",");
    const assignedToParam = patientDirectoryAssignedToQueryValue(assignedCsv);
    if (assignedToParam) {
      filters.assigned_to = assignedToParam;
    }

    if (fs.getAppliedTypes().length > 0) {
      filters.type = fs.getAppliedTypes().map((t) => t.value).join(",");
    }

    if (fs.getAppliedTags().length > 0) {
      filters.tags = fs.getAppliedTags().join(",");
    }

    if (fs.isEngageListClinicPreFilterApplied) {
      const patientIds = fs.getDosFiltersResult();
      filters.patientId = patientIds?.length ? patientIds.map((id) => String(id).trim()).join(",") : "";
    }

    this.mergeReferralCreatedDateRangeIntoFilters(filters, opts);

    const engageSearch = String(opts?.engageSearch ?? "").trim();
    if (engageSearch) {
      filters.engage_search = engageSearch;
      if (opts?.engageSearchFull) {
        filters.engage_search_full = true;
      }
    }

    return filters;
  }

  private getReferralsAfterListClinic(
    page: number,
    limit: number,
    opts?: EngageReferralsQueryPageOptions
  ): Observable<EngageReferralsResponse> {
    const fs = this._filterService;
    const baseFilters = this.buildPatientDirectoryQueryFilters(opts);

    const notifKeys = fs.getReferralNotificationFilterKeys();

    if (notifKeys.length > 0) {
      const filters: PatientDirectoryFilterValues = { ...baseFilters };
      for (const key of notifKeys) {
        filters[key] = true;
      }
      return this.getPatientDirectoryPage(page, limit, filters);
    }

    const hasNotifications = fs.getHasNotifications();
    const hasReminder = fs.getHasReminder();
    const hasStarred = fs.getHasStarred();
    const hasNewLead = fs.getHasNewLead();

    if (hasReminder) {
      const filters: PatientDirectoryFilterValues = {
        ...baseFilters,
        has_reminder: hasReminder,
      };
      return this.getPatientDirectoryPage(page, limit, filters);
    }

    const filters: PatientDirectoryFilterValues = { ...baseFilters };
    if (hasNotifications) {
      filters.has_notifications = hasNotifications;
    } else if (hasStarred === 1) {
      filters.starred = 1;
    }

    if (hasNewLead === true) {
      filters.has_new_lead = true;
    }

    return this.getPatientDirectoryPage(page, limit, filters);
  }

  private getFormattedEndDateString(date: Date): string {
    const dateStr = this.toYmd(date);
    const timezoneOffset = this.getTimezoneOffsetString(date);
    return `${dateStr}T23:59:59.999${timezoneOffset}`;
  }

  private getFormattedStartDateString(date: Date): string {
    const dateStr = this.toYmd(date);
    const timezoneOffset = this.getTimezoneOffsetString(date);
    return `${dateStr}T00:00:00.000${timezoneOffset}`;
  }

  private toYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  private getTimezoneOffsetString(date: Date): string {
    const offsetMinutes = date.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes <= 0 ? "+" : "-";
    return `${sign}${String(offsetHours).padStart(2, "0")}:${String(offsetMins).padStart(
      2,
      "0"
    )}`;
  }
}
